/**
 * Video practice consent (P5).
 *
 * Shown BEFORE the camera is requested. Nothing is asked of the browser until
 * the learner has read what video practice does and does not look at, and has
 * ticked the box: the camera permission prompt is the second step, never the
 * first.
 */

import {
  VIDEO_ANALYSED,
  VIDEO_NOT_ANALYSED,
  VIDEO_RETENTION_NOTICE,
  VIDEO_AVAILABILITY_NOTICE,
  VIDEO_CONSENT_LABEL,
} from './videoPracticeCopy';

export interface VideoConsentProps {
  consented: boolean;
  onConsentChange: (consented: boolean) => void;
  onContinue: () => void;
  /** Development or Testing — named so the learner knows what they are entering. */
  mode: 'development' | 'testing';
}

export function VideoConsent({ consented, onConsentChange, onContinue, mode }: VideoConsentProps) {
  return (
    <section aria-labelledby="video-consent-title" className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4">
      <div>
        <h3 id="video-consent-title" className="text-base font-semibold text-gray-900">Video practice</h3>
        <p className="mt-1 text-sm text-gray-600">
          You talk with the persona out loud, as in Live Conversation, with your camera on.
          {mode === 'testing' ? ' This is a graded test.' : ' This is a practice session with coaching.'}
        </p>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900">What it looks at</h4>
        <ul className="mt-1 list-disc pl-5 text-sm text-gray-700" data-testid="video-analysed">
          {VIDEO_ANALYSED.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>

      <div>
        <h4 className="text-sm font-medium text-gray-900">What it never looks at</h4>
        <ul className="mt-1 list-disc pl-5 text-sm text-gray-700" data-testid="video-not-analysed">
          {VIDEO_NOT_ANALYSED.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </div>

      <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700" data-testid="video-retention">{VIDEO_RETENTION_NOTICE}</p>
      <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700" data-testid="video-availability">{VIDEO_AVAILABILITY_NOTICE}</p>

      <label className="flex items-start gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={consented}
          onChange={(e) => onConsentChange(e.target.checked)}
          className="mt-0.5"
        />
        <span>{VIDEO_CONSENT_LABEL}</span>
      </label>

      <button
        type="button"
        onClick={onContinue}
        disabled={!consented}
        className="w-full rounded-xl bg-primary-600 px-4 py-3 text-sm font-medium text-white disabled:bg-gray-200 disabled:text-gray-400 sm:w-auto"
      >
        Turn on camera
      </button>
    </section>
  );
}

export default VideoConsent;
